<template>
  <div>
    <v-container>
      <h3>Reconciliation Details</h3>
      <v-data-table
        dense
        :headers="headers"
        :items-per-page="-1"
        :items="editedItem"
        item-key="rowKey"
        hide-default-footer
        class="elevation-1"
      >
        <template v-slot:top>
          <div class="d-flex justify-end">
            <v-btn
              class="mx-2 recon-details-add-new-item"
              color="primary" fab icon x-small
              @click="addOne"
            >
              <v-icon>
                {{icons.mdiPlus}}
              </v-icon>
          </v-btn>
          </div>
        </template>

        <template v-slot:[`item.bankCode`]="{ item, index }">
          <v-autocomplete
            v-model="item.bankCode"
            class="bank-recon-details-help-autocomplete"
            placeholder="Help"
            item-text="bankCodeStr"
            item-value="_id"
            :height="20"
            :items="bankCodesList"
            @change="onChangeBankCode(index), somethingChanged()"
          ></v-autocomplete>
        </template>

        <template v-slot:[`item.description`]="{ item }">
          <v-text-field
            v-model="item.description"
            v-uppercase=globalSettingItem.conEverythingToCaps
            class="bank-recon-details-description-text"
            label="Description"
            dense
            single-line
            @blur="somethingChanged"
          ></v-text-field>
        </template>

        <template v-slot:[`item.addDeduct`]="{ item }">
          <v-select
            v-model="item.addDeduct"
            class="bank-recon-details-add-deduct-code-select"
            label="Add Deduct Code"
            item-text="description"
            item-value="name"
            :items="addDeductCodeTypes"
            dense
            single-line
            :rules="reqdSelectValidation"
            @change="somethingChanged(); updateBalance()"
          ></v-select>
                </template>
          <template v-slot:[`item.amount`]="{ index }">
          <v-text-field
          dense
          single-line
          class="bank-recon-details-amount-text"
          label="Amount"
          v-model="editedItem[index].amount"
          @blur="updateDetailsAmount"
          @keydown.enter="updateDetailsAmount"
          @focus="$event.target.select()"
          @change="somethingChanged(); updateBalance()"
          ref="isAmountRef"
        />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon
            small class="mr-2"
            tabindex="-1"
            @click="removeOne(item)"
          >
            {{icons.mdiDelete}}
          </v-icon>
        </template>
        <template slot="body.append">
          <tr class="blue--text">
              <td id="total-trans-amount">
                Total Reconciliation Amount: ${{ amountFormat(amountRemaining) }}
              </td>
          </tr>
        </template>
      </v-data-table>
    </v-container>
  </div>
</template>

<script>
import {
  mapGetters, mapState,
} from 'vuex';
import {
  mdiPlus,
  mdiDelete,
} from '@mdi/js';
import {
  amountFormat,
  amountToNumber,
} from '../../../util/shared/vue-global';
import GlobalToCaps from '../../../util/shared/GlobalToCaps.mixin.vue';

const defaultItem = {
  bankCode: null,
  description: '',
  addDeduct: '',
  amount: 0,
};
const bankCodesCrudKey = 'bankCodes';

// The details are _id-less subdocuments, so the grid tracks each row by a generated key
// (kept on the row as `rowKey`, stripped by TransactionList before save). Rows that come
// from the server get theirs the first time they are shown; the key then travels with the
// row through every emit, so a row keeps its component however the array is rebuilt.
let rowKeySeed = 0;
const newRowKey = () => {
  rowKeySeed += 1;
  return `recon-row-${rowKeySeed}`;
};
const generatedRowKeys = new WeakMap();
const rowKeyOf = (row) => {
  if (row.rowKey) return row.rowKey;
  if (!generatedRowKeys.has(row)) generatedRowKeys.set(row, newRowKey());
  return generatedRowKeys.get(row);
};

export default {
  name: 'BankReconDetails',
  mixins: [GlobalToCaps],
  created() {
  },
  props: {
    baseCrudKey: {
      type: String,
      default: 'bankReconDetails-dialog',
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    icons: {
      mdiPlus,
      mdiDelete,
    },
    // Not sortable: the grid is edited in place, so a sorted column moved a row as soon as its
    // value changed and dropped a new (blank) row into the middle.
    headers: [
      { text: 'Help', value: 'bankCode', sortable: false },
      { text: 'Description', value: 'description', sortable: false },
      { text: 'Add Deduct Code', value: 'addDeduct', sortable: false },
      { text: 'Amount', value: 'amount', sortable: false },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
  }),
  computed: {
    ...mapGetters('base/crud', ['criteria', 'item', 'items']),
    ...mapState({
      addDeductBankReconTypes: (state) => state.enums.SaiBankReconAddDeductEnum,
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem,

    }),
    addDeductCodeTypes() {
      if (!this.addDeductBankReconTypes) {
        return [];
      }
      return this.addDeductBankReconTypes;
    },
    editedItem: {
      get() {
        const newVal = JSON.parse(JSON.stringify(this.value));
        const updatedValue = newVal.map((row, index) => ({
          ...this.loadInitialValues(row),
          rowKey: rowKeyOf(this.value[index]),
        }));
        return updatedValue;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    amountRemaining() {
      let totalRemainingReconAmount = amountToNumber(this.amount);
      for (let i = 0; i < this.editedItem.length; i += 1) {
        totalRemainingReconAmount += amountToNumber(this.editedItem[i].amount);
      }
      this.$emit('totalRemainingReconAmount', totalRemainingReconAmount);
      return totalRemainingReconAmount;
    },
    bankCodesList() {
      const bankCodesObjs = this.items(bankCodesCrudKey) || [];
      const updatedBankCodesObjs = bankCodesObjs.map((b) => ({
        ...b,
        bankCodeStr: ` ${b.description || ''} ${b.addDeduct || ''}`,
        bankCodeObj: {
          description: b.description || '',
          addDeduct: b.addDeduct || '',
        },
      }));
      return [{ _id: null, bankCodeStr: '' }, ...updatedBankCodesObjs];
    },
    reqdSelectValidation() {
      return [
        (value) => !!value || 'Required',
      ];
    },
  },
  methods: {
    amountFormat,
    amountToNumber,
    loadInitialValues(item) {
      return {
        ...item,
        amount: amountFormat(amountToNumber(item.amount)),
      };
    },
    addOne() {
      this.$emit('input', [
        ...this.editedItem,
        {
          ...defaultItem,
          rowKey: newRowKey(),
        },
      ]);
      return true;
    },
    removeOne(item) {
      this.$emit('input', [
        ...this.editedItem.filter((candidate) => item !== candidate),
      ]);
      this.$nextTick(() => {
        this.updateBalance();
      });
    },
    updateAmount(newValue, index) {
      if (!this.editedItem[index]) return;
      this.editedItem[index].amount = newValue;
    },
    updateDetailsAmount() {
      /* DEDUCT Type amount will be always negative */
      for (let i = 0; i < (this.editedItem || []).length; i += 1) {
        if (this.editedItem[i].addDeduct === 'DEDUCT') {
          this.editedItem[i].amount = Math.abs(amountToNumber(this.editedItem[i].amount)) * -1;
          // it's negative but prevents double negatives
        } else {
          this.editedItem[i].amount = Math.abs(amountToNumber(this.editedItem[i].amount));
          // Ensures positive amounts for non-DEDUCT
        }
      }
    },
    somethingChanged() {
      this.updateDetailsAmount();
      this.$emit('input', this.editedItem);
    },
    onChangeBankCode(index) {
      const updateBankCodesArray = [...this.bankCodesList];
      if (this.editedItem[index].bankCode != null) {
        const objectWithBankCode = updateBankCodesArray.find(
          (item) => item._id === this.editedItem[index].bankCode,
        );
        this.editedItem[index].addDeduct = objectWithBankCode.addDeduct;
        this.editedItem[index].description = objectWithBankCode.description;
      } else {
        this.editedItem[index].addDeduct = '';
        this.editedItem[index].description = '';
      }
      return this.editedItem[index];
    },
    updateBalance() {
      let bankBalance = 0.0;
      let addDeductBalance = 0.0;
      for (let i = 0; i < (this.editedItem || []).length; i += 1) {
        if (this.editedItem[i].addDeduct === 'BANKBAL') {
          bankBalance = amountToNumber(bankBalance) + amountToNumber(this.editedItem[i].amount);
        }
        addDeductBalance = amountToNumber(addDeductBalance)
           + amountToNumber(this.editedItem[i].amount);
      }
      this.$emit('addDeductBalance', amountFormat(addDeductBalance));
      this.$emit('bankBalance', amountFormat(bankBalance));
    },
  },
};
</script>

<style lang="sass">
  .eft-rec-amount input
    text-align: right
  .recon-details-add-new-item
    margin-top: 16px
  .bank-recon-details-help-autocomplete
    width: 18.5rem
  .bank-recon-details-description-text
    width: 25rem
  .bank-recon-details-add-deduct-code-select
    width: 6.5rem
</style>
